import { useEffect, useMemo, useRef, useState } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';

// Brand-new Unify UI library — no `@nearst/ui` or `@components/*` imports.
// The defensive heading rule in `index.css` (and the absence of these imports)
// keeps the legacy global SCSS out of this page entirely.
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { OnboardingStepper } from '@/components/onboarding/OnboardingStepper';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';

import { BusinessDetails } from './components/BusinessDetails';
import { POSConnection } from './components/POSConnection';
import { GoogleBusinessProfile } from './components/GoogleBusinessProfile';
import { resolveDeepLinkStep } from './lib/resolveDeepLinkStep';
import { completeOnboarding } from './lib/completeOnboarding';
import { findInventorySource, isPosConnectionComplete } from './utils/shopUtils';
import { CONNECTION_KIND } from './constants';
import { useBusinessDetails } from './hooks/useBusinessDetails';
import { useGoogleBusinessProfile } from './hooks/useGoogleBusinessProfile';
import { useShopInitialisation } from './hooks/useShopInitialisation';
import { useGBPConnectionPolling } from './hooks/useGBPConnectionPolling';
import { ConnectionProvider } from './context/ConnectionContext';
import { AssistantProvider } from '@services/AssistantProvider';
import { switchToOrganization } from '@services/auth';
import shopApi from '@services/shop';
import useClerkLoginState from '@utils/useClerkLoginState';

// `starter` / `pro` route the welcome CTA into auto-subscribe (welcome/index.jsx) —
// the only values that drive billing. Anything else (or no `?plan=` param) gets
// the `unknown-plan` sentinel so we can spot organic signups that didn't come
// through a pricing-card link.
const VALID_PLANS = ['starter', 'pro'];
const DEFAULT_PLAN = 'unknown-plan';

const GBP_ERROR_MESSAGES = {
	revoke_required:
		'Google has already authorised NearSt for this account, but we need to reconnect. Please ask the assistant for help or try again.',
	declined:
		'You declined the Google permission request. NearSt needs access to your Google Business Profile to verify your shop. Please try again and accept the permissions when prompted.',
	no_business:
		"We couldn't find a Google Business Profile linked to your Google account. Please make sure you're signed in to the correct Google account and try again.",
	mismatch:
		"Your Google Business Profile doesn't match the shop you're setting up. You may have signed in with the wrong Google account. Please try again with the correct account.",
	no_auth:
		"Your Google account doesn't have owner permissions for this business listing. The owner of the Google Business Profile needs to connect instead.",
	no_shop: "We couldn't find your shop. Please contact support@unify.near.st for help."
};

export const UnifyOnboarding = () => {
	useClerkLoginState();
	const { user, isLoaded, isSignedIn } = useUser();

	const { signOut } = useClerk();
	const navigate = useNavigate();
	const { shopPath } = useShopPath();
	const [searchParams] = useSearchParams();
	const [orgRestored, setOrgRestored] = useState(false);
	const [viewingStep, setViewingStep] = useState(null);
	// Applies `?step=N` once after the page's state resolves — see the effect
	// further down. A ref instead of a state so we don't re-trigger renders.
	const stepDeepLinkAppliedRef = useRef(false);
	// Same once-only semantics for the POS OAuth return (`?pos_callback=true`).
	// react-router's searchParams never observe history.replaceState, so without
	// the ref the effect would re-fire on every dependency change.
	const posCallbackAppliedRef = useRef(false);

	// The onboarding page never redirects *away* on its own — a retailer may
	// deliberately revisit it (e.g. for an already-set-up location) and should
	// be allowed to stay. Whether a shop is sent *to* onboarding is decided
	// solely by AppLayout via `shopNeedsOnboarding`, which is authoritative on
	// the per-shop `onboardingComplete` flag. `mutate` refreshes the shops
	// cache after completion so AppLayout stops routing here.
	const { mutate } = useShop();

	// Extract and validate plan from URL parameter
	const plan = useMemo(() => {
		const planParam = searchParams.get('plan');
		return VALID_PLANS.includes(planParam) ? planParam : DEFAULT_PLAN;
	}, [searchParams]);

	// Restore organisation context on page load if user has an org but no active org
	useEffect(() => {
		const restoreOrgContext = async () => {
			if (!isLoaded || orgRestored) return;
			// If no user, mark as restored so the page can continue
			if (!user) {
				setOrgRestored(true);
				return;
			}

			const orgs = user.organizationMemberships || [];
			if (orgs.length === 0) {
				setOrgRestored(true);
				return;
			}

			// Check if we already have an active org in the JWT
			const token = await window.Clerk.session?.getToken();
			if (token) {
				const payload = JSON.parse(atob(token.split('.')[1]));
				if (payload.org_slug) {
					setOrgRestored(true);
					return; // Already have active org
				}
			}

			// User has orgs but no active org - set the first one as active
			const firstOrg = orgs[0].organization;
			try {
				await switchToOrganization(firstOrg.id);
				setOrgRestored(true);
			} catch (err) {
				console.error('Failed to switch to organization:', err);
			}
		};

		restoreOrgContext();
	}, [isLoaded, user, orgRestored]);

	// Business Details
	const businessDetails = useBusinessDetails(user, plan);

	// Current shop state - only initialize after org context is restored
	const { currentShop, setCurrentShop, initializing } = useShopInitialisation(
		isLoaded && orgRestored,
		navigate,
		businessDetails.setBusinessDetailsCompleted,
		businessDetails.setAddressDetails,
		businessDetails.setFormData
	);

	// Function to refresh shop data
	const refreshShopData = async () => {
		if (!currentShop) return;
		try {
			const shops = await shopApi.getMyShops();
			const updatedShop = shops.find((s) => s.id === currentShop.id);
			if (updatedShop) {
				setCurrentShop(updatedShop);
			}
		} catch (err) {
			// Silently handle error
		}
	};

	// POS Connection: Track completion status (state managed by ConnectionContext).
	// The predicate lives in shopUtils so it can be table-tested: the old bare
	// `!!pointOfSale` check read the pre-OAuth placeholder as complete, which
	// unmounted the step (and the code exchange) the moment the browser returned
	// from the POS (NEA-5988).
	const posConnection = useMemo(() => {
		const stockSource = findInventorySource(currentShop, CONNECTION_KIND.STOCK);
		return {
			posConnectionCompleted: isPosConnectionComplete(stockSource)
		};
	}, [currentShop]);

	// Track POS connection state for chatbot context
	const [posConnectionState, setPosConnectionState] = useState(null);

	// Google Business Profile - enabled after business details
	const gbp = useGoogleBusinessProfile(businessDetails.businessDetailsCompleted, currentShop);

	// GBP connection polling
	useGBPConnectionPolling(gbp.pollingForGBPConnection, currentShop, refreshShopData, gbp.setPollingForGBPConnection, gbp.setGbpError);

	// Track page view and update Mixpanel super property for current step
	useEffect(() => {
		if (!orgRestored) return; // Wait until org context is restored

		const currentStep = businessDetails.businessDetailsCompleted
			? gbp.gbpCompleted
				? posConnection.posConnectionCompleted
					? 'completed'
					: '3'
				: '2'
			: '1';

		mixpanel.register({ 'Unify Onboarding Step': currentStep });
		mixpanel.track('Unify onboarding page view', { plan });
	}, [plan, businessDetails.businessDetailsCompleted, gbp.gbpCompleted, posConnection.posConnectionCompleted, orgRestored]);

	// Handler for completing onboarding manually. Branch logic lives in
	// `completeOnboarding` so it's unit-testable without the heavy page render.
	const handleCompleteOnboarding = () => completeOnboarding({ shop: currentShop, navigate, shopPath, mutate });

	// Populate form data from user and handle OAuth callbacks
	useEffect(() => {
		if (!isLoaded) return;

		// NB: we deliberately do NOT redirect on `user.unsafeMetadata.onboardingComplete`
		// here. That flag is user-global, but onboarding is per-shop: a user can have
		// completed onboarding for one location while another still needs it. Leaving
		// onboarding is owned solely by the shop-aware effect above (redirects away
		// only when the *active* shop no longer needs onboarding). Redirecting on the
		// global flag fought AppLayout's per-shop redirect and caused an infinite
		// /:shopId ⇄ /:shopId/onboarding loop (NEA-5724).

		// Check if returning from OAuth - use specific callback parameters
		const gbpCallback = searchParams.get('gbp_callback');
		const gbpError = searchParams.get('gbp_error');

		// Handle return from GBP OAuth
		if (gbpCallback === 'true') {
			if (gbpError) {
				gbp.setGbpError(
					GBP_ERROR_MESSAGES[gbpError] || 'Something went wrong connecting your Google Business Profile. Please try again.'
				);
				gbp.setGbpLoading(false);
			} else {
				// Start polling for GBP connection
				gbp.setPollingForGBPConnection(true);
				gbp.setGbpLoading(false);
			}

			// Clean the callback params pathname-preservingly — hard-coding
			// '/onboarding' rewrote the shop-scoped /:shopId/onboarding URL back to
			// the flat form, so a refresh resolved an arbitrary sibling shop.
			const next = new URL(window.location.href);
			next.searchParams.delete('gbp_callback');
			next.searchParams.delete('gbp_error');
			window.history.replaceState({}, '', next.pathname + next.search);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded, user, isSignedIn, navigate, searchParams]);

	// POS OAuth return (`?pos_callback=true`): force the Inventory step open so
	// ConnectionProvider mounts — it hosts useOAuthCallback, which exchanges the
	// code and surfaces the outlet picker. Without this, a shop whose eager
	// placeholder (or completed connection) makes allComplete true renders the
	// celebration CTA instead, and the code is never exchanged. Gated on the same
	// preconditions as the ?step=3 deep-link so a stale bookmarked callback URL
	// can't open a locked POS card mid-registration.
	useEffect(() => {
		if (posCallbackAppliedRef.current) return;
		if (initializing || !orgRestored) return;
		if (searchParams.get('pos_callback') !== 'true') return;
		if (!businessDetails.businessDetailsCompleted || !gbp.gbpCompleted) return;
		posCallbackAppliedRef.current = true;
		setViewingStep(2);
		// Strip only pos_callback (pathname-preservingly) — code/state/connectSessionId
		// must survive for useOAuthCallback to consume.
		const next = new URL(window.location.href);
		next.searchParams.delete('pos_callback');
		window.history.replaceState({}, '', next.pathname + next.search);
	}, [initializing, orgRestored, searchParams, businessDetails.businessDetailsCompleted, gbp.gbpCompleted]);

	// `?step=2` (GBP) and `?step=3` (POS) are the deep-links the health
	// banners use to jump returning users directly to the broken step. The
	// resolution logic (which step to display, and the preconditions that
	// must be met) lives in `resolveDeepLinkStep` so it can be unit-tested
	// independent of the heavy onboarding-page state machine.
	useEffect(() => {
		if (stepDeepLinkAppliedRef.current) return;
		if (initializing || !orgRestored) return;
		const targetIndex = resolveDeepLinkStep(searchParams.get('step'), {
			businessDetailsCompleted: businessDetails.businessDetailsCompleted,
			gbpCompleted: gbp.gbpCompleted
		});
		if (targetIndex === null) return;
		setViewingStep(targetIndex);
		stepDeepLinkAppliedRef.current = true;
		// Clean the URL so refresh / back doesn't re-apply the deep-link.
		const next = new URL(window.location.href);
		next.searchParams.delete('step');
		window.history.replaceState({}, '', next.pathname + next.search);
	}, [initializing, orgRestored, searchParams, businessDetails.businessDetailsCompleted, gbp.gbpCompleted]);

	const handleBusinessDetailsSubmit = async () => {
		try {
			const shop = await businessDetails.handleGetStarted();

			// No shop means no shop was created, so the step is not done. Marking
			// it complete here regardless is what used to advance a failed sign-up
			// past the form and strand the retailer on an empty store picker with
			// the error hidden (NEA-6503).
			if (!shop) return;

			// Set shop immediately to prevent race condition
			// The create-shop response includes legacyId needed for GBP connection
			setCurrentShop(shop);

			// Optionally enrich with supplemental data in the background
			try {
				const shops = await shopApi.getMyShops();
				const fullShop = shops.find((s) => s.id === shop.id);
				// Only update if enriched data includes legacyId to avoid eventual consistency issues
				if (fullShop?.legacyId) {
					setCurrentShop(fullShop);
				}
			} catch (err) {
				// Shop already set with valid data, continue
			}

			businessDetails.setBusinessDetailsCompleted(true);
		} catch (err) {
			// Error already handled in the hook
		}
	};

	const handleSignOut = async () => {
		await signOut();
		// Redirect to login page after sign-out
		navigate('/login');
	};

	const handleSignIn = () => {
		navigate('/login');
	};

	if (initializing) {
		return <Loader label="Setting up your account…" />;
	}

	const error = businessDetails.error;
	const warning = null;

	// `posConnectionState` and the legacy assistant integration are kept on
	// the page (see hooks below) so a follow-up ticket can wire a Unify-native
	// onboarding assistant back in without rewiring data — but the chat UI
	// itself is intentionally not rendered until that ticket lands.
	void posConnectionState;

	const stepperSteps = [
		{ id: 'details', shortLabel: 'Business details', done: businessDetails.businessDetailsCompleted },
		{ id: 'gbp', shortLabel: 'Google Business', done: gbp.gbpCompleted },
		{ id: 'pos', shortLabel: 'Inventory', done: posConnection.posConnectionCompleted }
	];
	const currentStepIndex = !businessDetails.businessDetailsCompleted ? 0 : !gbp.gbpCompleted ? 1 : 2;
	const allComplete = businessDetails.businessDetailsCompleted && gbp.gbpCompleted && posConnection.posConnectionCompleted;
	// `displayedStep` lets the user click back to a confirmed step in the
	// stepper without losing forward progress. While `viewingStep` is null
	// we render whatever the natural progression says (or nothing when
	// everything is done — in that case the celebration CTA takes over).
	const displayedStep = viewingStep ?? (allComplete ? null : currentStepIndex);
	const handleStepClick = (index) => {
		if (!stepperSteps[index].done && index !== currentStepIndex) return;
		setViewingStep(index === currentStepIndex && !allComplete ? null : index);
	};

	return (
		<AssistantProvider mode="onboarding" currentShop={currentShop} userId={user?.id}>
			<OnboardingShell isSignedIn={isSignedIn} onSignOut={handleSignOut} onSignIn={handleSignIn}>
				<div className="mb-10 text-center">
					<p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">Welcome to Unify</p>
					<h1 className="mt-2 text-3xl font-extrabold tracking-tight md:text-[34px]">Let&apos;s see how easy this is</h1>
					<p className="mx-auto mt-2 max-w-[480px] text-base text-muted-foreground">
						Three quick steps and you&apos;re ready to go live.
					</p>
				</div>

				<OnboardingStepper
					steps={stepperSteps}
					currentStep={currentStepIndex}
					viewingStep={viewingStep}
					onStepClick={handleStepClick}
				/>

				{error && (
					<div
						className="mb-6 rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
						data-cy="error-message"
					>
						{error}
					</div>
				)}

				{warning && !error && (
					<div className="mb-6 rounded-2xl border border-border bg-secondary px-4 py-3 text-sm text-muted-foreground">
						{warning}
					</div>
				)}

				<div className="space-y-5">
					{displayedStep === 0 && (
						<BusinessDetails
							isCompleted={businessDetails.businessDetailsCompleted}
							isExpanded={businessDetails.businessDetailsExpanded}
							isEditing={businessDetails.isEditing}
							selectedPlace={businessDetails.selectedPlace}
							addressDetails={businessDetails.addressDetails}
							formData={businessDetails.formData}
							loading={businessDetails.loading}
							validationErrors={businessDetails.validationErrors}
							onPlaceSelect={businessDetails.handlePlaceSelect}
							onFormChange={businessDetails.handleFormChange}
							onFieldBlur={businessDetails.handleFieldBlur}
							onSubmit={handleBusinessDetailsSubmit}
							onEdit={businessDetails.handleEdit}
							onCancel={businessDetails.handleCancel}
							onChangeStore={businessDetails.handleChangeStore}
							isSignedIn={businessDetails.isSignedIn}
							showPassword={businessDetails.showPassword}
							authError={businessDetails.authError}
							onEmailBlur={businessDetails.handleEmailBlur}
							isAuthenticating={businessDetails.isAuthenticating}
						/>
					)}

					{displayedStep === 1 && (
						<GoogleBusinessProfile
							isActive={isSignedIn && businessDetails.businessDetailsCompleted && !gbp.gbpCompleted}
							isCompleted={gbp.gbpCompleted}
							businessDetailsCompleted={isSignedIn && businessDetails.businessDetailsCompleted}
							gbpLoading={gbp.gbpLoading}
							pollingForGBPConnection={gbp.pollingForGBPConnection}
							gbpError={gbp.gbpError}
							currentShop={currentShop}
							onConnect={gbp.handleConnectGBP}
						/>
					)}

					{displayedStep === 2 && (
						<ConnectionProvider
							businessDetailsCompleted={isSignedIn && businessDetails.businessDetailsCompleted && gbp.gbpCompleted}
							currentShop={currentShop}
							onShopRefresh={refreshShopData}
							onConnectionStateChange={setPosConnectionState}
						>
							<POSConnection
								isActive={
									isSignedIn &&
									businessDetails.businessDetailsCompleted &&
									gbp.gbpCompleted &&
									!posConnection.posConnectionCompleted
								}
								isCompleted={posConnection.posConnectionCompleted}
								businessDetailsCompleted={isSignedIn && businessDetails.businessDetailsCompleted && gbp.gbpCompleted}
							/>
						</ConnectionProvider>
					)}
				</div>

				{allComplete && (
					<div className="mt-8 flex flex-col items-center text-center">
						<p className="text-sm text-muted-foreground">You&apos;re all set — let&apos;s get you ready to launch.</p>
						<Button onClick={handleCompleteOnboarding} className="mt-4 rounded-full px-6">
							Complete Unify onboarding
						</Button>
					</div>
				)}

				<p className="mt-8 text-center text-xs text-muted-foreground">You can edit any of this later in Settings.</p>
			</OnboardingShell>
		</AssistantProvider>
	);
};
